import axios, {
  AxiosError,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from "axios";
import { loginRequest, msalInstance } from "src/auth/auth-config";
import { ERROR_403_MESSAGE } from "./constants";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const baseConfig: CreateAxiosDefaults = {
  // Auth is entirely bearer-token (see the Authorization header interceptor
  // below) — there's no cookie-based session anywhere in the backend.
  // withCredentials:true only causes the browser to attach cookies
  // (including third-party analytics cookies) to these cross-origin API
  // calls, which was tripping WAF SQL-injection false positives.
  withCredentials: false,
};

export const instanceWithoutInterceptors = axios.create(baseConfig);

export const createInstance = (
  token: string | null,
  setToken: (arg0: string) => void
) => {
  const instance = axios.create(baseConfig);

  instance.interceptors.request.use(
    function (config) {
      const accessToken = token;
      // Dev-mode bypass: a stub token of "local-dev-bypass-token" means
      // we're running with VITE_LOCAL_MODE=true. The BE's JWT middleware
      // would reject this as "Invalid token format" — so we deliberately
      // omit the Authorization header in that case, which the BE
      // BannerFilterMiddleware treats as a dev request and grants all
      // banners.
      if (accessToken && accessToken !== "local-dev-bypass-token") {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    },
    function (error) {
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  );

  instance.interceptors.response.use(
    function (response) {
      return response;
    },
    async function (error: AxiosError) {
      const originalRequest: CustomAxiosRequestConfig | undefined =
        error.config;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
          }
          const { accessToken } = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: msalInstance.getActiveAccount() || undefined,
          });
          setToken(accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          return instance(originalRequest);
        } catch (refreshError) {
          msalInstance.loginRedirect(loginRequest);
          return Promise.reject(refreshError);
        }
      } else if (error.response?.status === 403) {
        /**
         * Keep the friendly message for the error boundary, which matches on
         * it — but do NOT throw the server's answer away with it.
         *
         * A bare `new Error(...)` has no `.response`, so extractApiError could
         * not read anything and every 403 in the app came out as "An
         * unexpected error occurred." The API is far more specific than that:
         *
         *   {"error":"Forbidden",
         *    "detail":"User 1179482 does not have Insert permission on
         *              RETAIL_PRICE.",
         *    "table":"RETAIL_PRICE","op":"Insert"}
         *
         * That sentence is the whole point of the RP-PERM test cases, several
         * of which assert the user can SEE it. Carrying `response` across keeps
         * the boundary behaviour identical and gives every other caller the
         * detail back.
         */
        const forbidden = new Error(ERROR_403_MESSAGE) as Error & {
          response?: AxiosError["response"];
          isAxiosError?: boolean;
        };
        forbidden.response = error.response;
        forbidden.isAxiosError = true;
        throw forbidden;
      }

      return Promise.reject(error);
    }
  );
  return instance;
};
